import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { 
  User, Phone, Mail, MapPin, CheckCircle, Zap, Target, Award, 
  Shield, ArrowRight, ArrowLeft, Check, Edit3, Calendar, Clock, Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function BookDemo() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    demoDate: '',
    preferredTime: 'evening',
    experience: 'beginner'
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFiring, setIsFiring] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Minimum date for demo is today
  const todayIso = new Date().toISOString().split('T')[0];

  // Sound effect on submit
  const playImpactEffect = () => {
    try {
      const audio = new Audio('https://www.soundjay.com/mechanical/gun-gunshot-01.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {}
    setIsFiring(true);
    setTimeout(() => setIsFiring(false), 800);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Step-by-Step Validation
  const validateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (!formData.name.trim()) errors.name = 'Full name is required';
      if (!formData.age || isNaN(formData.age) || Number(formData.age) < 5 || Number(formData.age) > 90) {
        errors.age = 'Please enter a valid age (5 - 90)';
      }
      if (!formData.gender) errors.gender = 'Please select a gender';
    } else if (step === 2) {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (!cleanPhone.match(/^[0-9]{10}$/)) errors.phone = 'Enter a valid 10-digit mobile number';
      if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) errors.email = 'Enter a valid email address';
      if (!formData.address.trim()) errors.address = 'Address/Location is required';
    } else if (step === 3) {
      if (!formData.demoDate) errors.demoDate = 'Preferred demo date is required';
      else {
        const selectedDate = new Date(formData.demoDate);
        if (selectedDate.getDay() === 1) {
          errors.demoDate = 'We are closed on Mondays. Please select another day (Tue-Sun)';
        }
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      alert('Please fill out all required fields in earlier steps.');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "demoBookings"), {
        ...formData,
        fullName: formData.name,
        phone: formData.phone.replace(/\D/g, ''),
        contactStatus: 'Pending',
        timestamp: serverTimestamp()
      });
      
      playImpactEffect();
      setSubmitted(true);
    } catch (error) {
      console.error("Booking submission error:", error);
      // Fallback direct WhatsApp redirect
      const fallbackUrl = `https://wa.me/919653524055?text=Hi%20Chaitanya%20Shooting%20Academy,%20I%20want%20to%20register%20for%20a%20Free%20Trial.%0AName:%20${encodeURIComponent(formData.name)}%0AAge:%20${encodeURIComponent(formData.age)}%0AGender:%20${encodeURIComponent(formData.gender)}%0APhone:%20${encodeURIComponent(formData.phone)}%0ADate:%20${encodeURIComponent(formData.demoDate)}%20(${encodeURIComponent(formData.preferredTime)})`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      playImpactEffect();
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const stepsList = [
    { id: 1, name: 'Personal' },
    { id: 2, name: 'Contact' },
    { id: 3, name: 'Training' },
    { id: 4, name: 'Submit' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 sm:p-6 text-center relative overflow-hidden pt-32 pb-24">
        <AnimatePresence>
          {isFiring && (
            <motion.div
              initial={{ x: "-100vw", opacity: 1 }}
              animate={{ x: "100vw", opacity: 0 }}
              transition={{ duration: 0.3, ease: "linear" }}
              className="absolute top-1/2 left-0 w-full h-[2px] bg-gold z-[100] shadow-[0_0_20px_rgba(255,215,0,0.8)]"
            />
          )}
        </AnimatePresence>

        <motion.div 
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-gradient-to-br from-zinc-900 to-black p-8 sm:p-12 rounded-[2.5rem] border-2 border-green-500/50 max-w-lg shadow-[0_0_50px_rgba(34,197,94,0.3)] w-full"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center"
          >
            <CheckCircle className="text-green-500" size={40} />
          </motion.div>

          <h2 className="text-white text-3xl sm:text-4xl font-black italic uppercase mb-2 tracking-tighter leading-none font-heading">
            TARGET <span className="text-green-500">ACQUIRED!</span>
          </h2>
          <p className="text-zinc-400 text-xs uppercase tracking-widest font-bold mb-6">
            Registration Submitted Successfully
          </p>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 text-left space-y-2">
            <div className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
              <span className="text-zinc-400">Shooter Name:</span>
              <span className="text-white font-bold">{formData.name}</span>
            </div>
            <div className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
              <span className="text-zinc-400">Age & Gender:</span>
              <span className="text-white font-bold">{formData.age} Yrs ({formData.gender})</span>
            </div>
            <div className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
              <span className="text-zinc-400">Phone:</span>
              <span className="text-white font-bold">+91 {formData.phone}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400">Scheduled Date:</span>
              <span className="text-gold font-bold">{formData.demoDate} ({formData.preferredTime})</span>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <a 
              href="tel:+919653524055"
              className="block w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all shadow-lg shadow-red-600/30"
            >
              📞 Call Academy (+91 96535 24055)
            </a>
            <a 
              href={`https://wa.me/919653524055?text=${encodeURIComponent(`Hi, I just submitted shooter registration for ${formData.name}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-[#25D366] hover:bg-[#20bd5a] text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all shadow-lg shadow-[#25D366]/30"
            >
              💬 WhatsApp Confirmation
            </a>
            <Link 
              to="/"
              className="block w-full bg-white/10 hover:bg-white/20 text-white py-3.5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all border border-white/10"
            >
              Return to Homepage
            </Link>
          </div>

          <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest">
            ⏱️ Academy coaches will confirm your target lane within 24 hours
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <section className="min-h-screen pt-28 sm:pt-32 pb-24 bg-[#050505] px-4 sm:px-6 relative overflow-hidden">
      {/* Glow Accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] bg-red-600/5 blur-[120px] rounded-full -z-10" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gold/5 blur-[100px] rounded-full -z-10" />

      <div className="max-w-4xl mx-auto">
        {/* TOP TITLE HEADER */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-600/10 border border-red-600/20 mb-3">
            <Zap size={14} className="text-red-600 animate-pulse" />
            <span className="text-[10px] font-black tracking-[0.35em] uppercase text-red-500">Official Shooter Portal</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl font-black italic uppercase text-white tracking-tighter">
            Shooter <span className="text-gold">Registration</span> Form
          </h1>
          <p className="text-zinc-400 text-xs sm:text-sm mt-2 font-medium">
            Complete the 4-step registration to reserve your lane slot at Chaitanya Shooting Academy
          </p>
        </div>

        {/* MAIN MULTI-STEP WIZARD CONTAINER */}
        <div className="bg-gradient-to-br from-zinc-900/90 via-black to-zinc-950 border border-white/10 p-5 sm:p-10 rounded-[2.5rem] shadow-2xl backdrop-blur-xl">
          
          {/* STEP WIZARD PROGRESS BAR */}
          <div className="mb-10 sm:mb-12">
            <div className="relative flex items-center justify-between max-w-xl mx-auto">
              {/* Connecting Progress Bar Line */}
              <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-[3px] bg-zinc-800 -z-0">
                <motion.div 
                  className="h-full bg-gradient-to-r from-red-600 to-gold"
                  animate={{ width: `${((currentStep - 1) / 3) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Step Circles */}
              {stepsList.map((step) => {
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <div key={step.id} className="relative z-10 flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (step.id < currentStep) setCurrentStep(step.id);
                      }}
                      disabled={step.id > currentStep}
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-black text-sm transition-all duration-300 ${
                        isCompleted 
                          ? 'bg-green-500 text-black shadow-[0_0_15px_rgba(34,197,94,0.5)] cursor-pointer' 
                          : isActive 
                            ? 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-[0_0_20px_rgba(220,38,38,0.6)] ring-4 ring-red-600/30 scale-110' 
                            : 'bg-zinc-800 text-zinc-500 border border-white/10'
                      }`}
                    >
                      {isCompleted ? <Check size={20} strokeWidth={3} /> : step.id}
                    </button>
                    <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest mt-2 transition-colors ${
                      isActive ? 'text-gold' : isCompleted ? 'text-white' : 'text-zinc-600'
                    }`}>
                      {step.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* STEP FORM CONTENT */}
          <form onSubmit={(e) => e.preventDefault()}>
            <AnimatePresence mode="wait">
              {/* STEP 1: PERSONAL DETAILS */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <div className="border-b border-white/10 pb-4 mb-6">
                    <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white flex items-center gap-2">
                      <User className="text-gold" size={22} /> Step 1: Personal Details
                    </h3>
                    <p className="text-zinc-400 text-xs mt-1">Please provide basic personal information of the shooter.</p>
                  </div>

                  {/* Full Name */}
                  <div>
                    <label htmlFor="name" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="name"
                      type="text" 
                      name="name"
                      placeholder="Your Name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all placeholder:text-zinc-600 ${
                        formErrors.name ? 'border-red-600 focus:ring-1 focus:ring-red-600' : 'border-white/10 focus:border-gold'
                      }`} 
                    />
                    {formErrors.name && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.name}</p>}
                  </div>

                  {/* Age & Gender Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="age" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                        Age <span className="text-red-500">*</span>
                      </label>
                      <input 
                        id="age"
                        type="number" 
                        name="age"
                        min="5"
                        max="90"
                        placeholder="Your Age" 
                        value={formData.age}
                        onChange={handleInputChange}
                        className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all placeholder:text-zinc-600 ${
                          formErrors.age ? 'border-red-600 focus:ring-1 focus:ring-red-600' : 'border-white/10 focus:border-gold'
                        }`} 
                      />
                      {formErrors.age && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.age}</p>}
                    </div>

                    <div>
                      <label htmlFor="gender" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <select 
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all ${
                          formErrors.gender ? 'border-red-600' : 'border-white/10 focus:border-gold'
                        }`}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      {formErrors.gender && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.gender}</p>}
                    </div>
                  </div>

                  {/* STEP 1 ACTION BUTTON */}
                  <div className="pt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleNext}
                      className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-10 py-4 rounded-xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-600/30"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP 2: CONTACT DETAILS */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <div className="border-b border-white/10 pb-4 mb-6">
                    <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white flex items-center gap-2">
                      <Phone className="text-gold" size={22} /> Step 2: Contact Details
                    </h3>
                    <p className="text-zinc-400 text-xs mt-1">We need contact details to confirm your lane timing slot.</p>
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                      Phone Number (10 digits) <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="phone"
                      type="tel" 
                      name="phone"
                      placeholder="10-digit Mobile Number" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all placeholder:text-zinc-600 ${
                        formErrors.phone ? 'border-red-600' : 'border-white/10 focus:border-gold'
                      }`} 
                    />
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.phone}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="email"
                      type="email" 
                      name="email"
                      placeholder="your@email.com" 
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all placeholder:text-zinc-600 ${
                        formErrors.email ? 'border-red-600' : 'border-white/10 focus:border-gold'
                      }`} 
                    />
                    {formErrors.email && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.email}</p>}
                  </div>

                  {/* Address */}
                  <div>
                    <label htmlFor="address" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                      Complete Address / Area <span className="text-red-500">*</span>
                    </label>
                    <textarea 
                      id="address"
                      name="address"
                      rows="2"
                      placeholder="E.g. Sector 81 / Vipul Lavanya, Gurugram" 
                      value={formData.address}
                      onChange={handleInputChange}
                      className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all resize-none placeholder:text-zinc-600 ${
                        formErrors.address ? 'border-red-600' : 'border-white/10 focus:border-gold'
                      }`} 
                    />
                    {formErrors.address && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.address}</p>}
                  </div>

                  {/* STEP 2 ACTION BUTTONS */}
                  <div className="pt-6 flex items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="bg-white/10 hover:bg-white/20 text-white px-6 py-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>

                    <button
                      type="button"
                      onClick={handleNext}
                      className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-10 py-4 rounded-xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center gap-2 shadow-lg shadow-red-600/30"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP 3: TRAINING PREFERENCES */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <div className="border-b border-white/10 pb-4 mb-6">
                    <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white flex items-center gap-2">
                      <Target className="text-gold" size={22} /> Step 3: Training Options
                    </h3>
                    <p className="text-zinc-400 text-xs mt-1">Select your preferred date, slot, and shooting background.</p>
                  </div>

                  {/* Preferred Demo Date */}
                  <div>
                    <label htmlFor="demoDate" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                      Preferred Date (Tue - Sun) <span className="text-red-500">*</span>
                    </label>
                    <input 
                      id="demoDate"
                      type="date" 
                      name="demoDate"
                      min={todayIso}
                      value={formData.demoDate}
                      onChange={handleInputChange}
                      style={{ colorScheme: 'dark' }}
                      className={`w-full bg-black/80 border p-4 rounded-xl text-white outline-none font-bold text-sm transition-all cursor-pointer ${
                        formErrors.demoDate ? 'border-red-600' : 'border-white/10 focus:border-gold'
                      }`} 
                    />
                    {formErrors.demoDate && <p className="text-red-500 text-xs mt-1.5 font-medium">{formErrors.demoDate}</p>}
                  </div>

                  {/* Time & Experience Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="preferredTime" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                        Preferred Time Slot
                      </label>
                      <select 
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full bg-black/80 border border-white/10 p-4 rounded-xl text-white outline-none font-bold text-sm focus:border-gold transition-all"
                      >
                        <option value="morning">Morning (8 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                        <option value="evening">Evening (4 PM - 8 PM)</option>
                        <option value="weekend">Weekend Special Slot</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="experience" className="text-xs uppercase font-bold text-zinc-300 tracking-wider mb-2 block">
                        Experience Level
                      </label>
                      <select 
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        className="w-full bg-black/80 border border-white/10 p-4 rounded-xl text-white outline-none font-bold text-sm focus:border-gold transition-all"
                      >
                        <option value="beginner">Beginner (First Time)</option>
                        <option value="intermediate">Intermediate (1-2 Years)</option>
                        <option value="advanced">Advanced Shooter (3+ Years)</option>
                      </select>
                    </div>
                  </div>

                  {/* STEP 3 ACTION BUTTONS */}
                  <div className="pt-6 flex items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="bg-white/10 hover:bg-white/20 text-white px-6 py-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>

                    <button
                      type="button"
                      onClick={handleNext}
                      className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-10 py-4 rounded-xl font-black text-xs uppercase tracking-[0.2em] transition-all flex items-center gap-2 shadow-lg shadow-red-600/30"
                    >
                      Review & Submit <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              )}

              {/* STEP 4: SUBMIT & SUMMARY REVIEW */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <div className="border-b border-white/10 pb-4 mb-6">
                    <h3 className="text-xl sm:text-2xl font-black italic uppercase text-white flex items-center gap-2">
                      <CheckCircle className="text-green-500" size={22} /> Step 4: Review Your Information
                    </h3>
                    <p className="text-zinc-400 text-xs mt-1">Please verify your details before submitting your registration.</p>
                  </div>

                  {/* SUMMARY CARDS */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Card 1: Personal */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 relative group">
                      <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-3">
                        <span className="text-[10px] font-black uppercase text-gold tracking-wider">Personal</span>
                        <button type="button" onClick={() => setCurrentStep(1)} className="text-zinc-400 hover:text-white p-1">
                          <Edit3 size={14} />
                        </button>
                      </div>
                      <p className="text-white font-bold text-sm">{formData.name || 'Not specified'}</p>
                      <p className="text-zinc-400 text-xs mt-1">{formData.age} Years • {formData.gender}</p>
                    </div>

                    {/* Card 2: Contact */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 relative group">
                      <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-3">
                        <span className="text-[10px] font-black uppercase text-gold tracking-wider">Contact</span>
                        <button type="button" onClick={() => setCurrentStep(2)} className="text-zinc-400 hover:text-white p-1">
                          <Edit3 size={14} />
                        </button>
                      </div>
                      <p className="text-white font-bold text-xs truncate">+91 {formData.phone}</p>
                      <p className="text-zinc-400 text-xs truncate mt-0.5">{formData.email}</p>
                      <p className="text-zinc-400 text-[11px] truncate mt-0.5">{formData.address}</p>
                    </div>

                    {/* Card 3: Training */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 relative group">
                      <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-3">
                        <span className="text-[10px] font-black uppercase text-gold tracking-wider">Training</span>
                        <button type="button" onClick={() => setCurrentStep(3)} className="text-zinc-400 hover:text-white p-1">
                          <Edit3 size={14} />
                        </button>
                      </div>
                      <p className="text-white font-bold text-xs">Date: {formData.demoDate}</p>
                      <p className="text-zinc-400 text-xs mt-0.5 capitalize">Slot: {formData.preferredTime}</p>
                      <p className="text-zinc-400 text-[11px] mt-0.5 capitalize">Level: {formData.experience}</p>
                    </div>
                  </div>

                  {/* STEP 4 FINAL SUBMIT BUTTONS */}
                  <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white px-6 py-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
                    >
                      <ArrowLeft size={16} /> Back
                    </button>

                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="w-full sm:w-auto bg-gradient-to-r from-red-600 via-red-500 to-red-700 hover:from-white hover:to-white hover:text-black text-white px-12 py-5 rounded-2xl font-black text-xs uppercase tracking-[0.25em] transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(220,38,38,0.5)]"
                    >
                      <Target size={18} />
                      {loading ? 'RESERVING YOUR SLOT...' : 'CONFIRM REGISTRATION'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </section>
  );
}